import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Target, ListChecks, Check } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", cadence: "weekly", target_date: "", progress: 0 });

  const load = async () => {
    try {
      const res = await api.get("/goals");
      setGoals(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.title.trim()) {
      toast.error("Name your goal");
      return;
    }
    try {
      const res = await api.post("/goals", form);
      setGoals((prev) => [res.data, ...prev]);
      setOpen(false);
      setForm({ title: "", description: "", cadence: "weekly", target_date: "", progress: 0 });
      toast.success("Goal added to the strategy board");
    } catch (e) {
      toast.error("Could not create goal");
    }
  };

  const updateProgress = async (goal, value) => {
    try {
      const res = await api.patch(`/goals/${goal.id}`, { progress: value });
      setGoals((prev) => prev.map((g) => (g.id === goal.id ? res.data : g)));
    } catch (e) {
      toast.error("Could not update goal");
    }
  };

  const remove = async (goal) => {
    try {
      await api.delete(`/goals/${goal.id}`);
      setGoals((prev) => prev.filter((g) => g.id !== goal.id));
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const replaceGoal = (updated) => {
    setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
  };

  return (
    <div className="space-y-10" data-testid="goals-root">
      <header className="flex items-end justify-between">
        <div>
          <p className="label-eyebrow">Strategy</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Goals board</h1>
          <p className="text-[#888] mt-4 max-w-xl">
            The things you’ve formally tabled. Review them at every session — adjust the dial, do not forget the dial.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-10 px-4"
          data-testid="add-goal-button"
        >
          <Plus size={14} className="mr-1.5" /> Add goal
        </Button>
      </header>

      {loading ? (
        <div className="surface rounded-md p-8 text-sm text-[#8a8a8a]">Loading…</div>
      ) : goals.length === 0 ? (
        <div className="surface rounded-md p-16 text-center" data-testid="goals-empty">
          <Target size={24} className="text-[#c89f45] mx-auto mb-4" />
          <p className="font-serif text-2xl">An empty board is a dangerous one.</p>
          <p className="text-[#888] mt-2 text-sm">Add what you are holding yourself to — weekly or monthly.</p>
          <Button onClick={() => setOpen(true)} className="mt-6 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a]" data-testid="empty-add-goal">
            <Plus size={14} className="mr-1.5" /> First goal
          </Button>
        </div>
      ) : (
        <ul className="space-y-3">
          {goals.map((g) => (
            <li key={g.id} className="surface rounded-md p-6" data-testid={`goal-row-${g.id}`}>
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="label-eyebrow">{g.cadence}</span>
                    {g.target_date && <span className="text-[10px] font-mono text-[#8a8a8a]">DUE {new Date(g.target_date).toLocaleDateString()}</span>}
                    {g.source === "session" && (
                      g.source_meeting_id ? (
                        <Link
                          to={`/meetings/${g.source_meeting_id}/minutes`}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#c89f45]/10 text-[#c89f45] hover:bg-[#c89f45]/20 transition-colors"
                          data-testid={`goal-source-${g.id}`}
                        >
                          From session
                        </Link>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[#c89f45]/10 text-[#c89f45]" data-testid={`goal-source-${g.id}`}>
                          From session
                        </span>
                      )
                    )}
                  </div>
                  <h3 className="font-serif text-2xl mt-2">{g.title}</h3>
                  {g.description && <p className="text-sm text-[#888] mt-2 max-w-2xl">{g.description}</p>}
                </div>
                <button onClick={() => remove(g)} className="text-[#8a8a8a] hover:text-red-400 transition-colors" data-testid={`delete-goal-${g.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="mt-6">
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <Slider
                      value={[g.progress]}
                      onValueChange={(v) => updateProgress(g, v[0])}
                      max={100}
                      step={5}
                      disabled={Array.isArray(g.tasks) && g.tasks.length > 0}
                      data-testid={`goal-slider-${g.id}`}
                    />
                  </div>
                  <span
                    className={`font-mono text-sm w-12 text-right ${g.progress === 100 ? "text-[#9bd3c4]" : "text-[#c89f45]"}`}
                    data-testid={`goal-progress-${g.id}`}
                  >
                    {g.progress}%
                  </span>
                </div>
                {Array.isArray(g.tasks) && g.tasks.length > 0 && (
                  <p className="text-[10px] font-mono text-[#8a8a8a] mt-2" data-testid={`goal-task-count-${g.id}`}>
                    {g.tasks.filter((t) => t.done).length} / {g.tasks.length} TASKS DONE · SLIDER LOCKED (driven by tasks)
                  </p>
                )}
              </div>

              <GoalTasks goal={g} onUpdated={replaceGoal} />
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-[#2a2a2a] text-[#fafafa]" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Table a goal</DialogTitle>
            <DialogDescription className="text-[#888]">
              State it clearly. The board will hold you to it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-eyebrow">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ship MVP by end of month"
                className="mt-2 bg-[#121212] border-[#2a2a2a] focus:border-[#c89f45]"
                data-testid="goal-title-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Why this matters and what done looks like."
                className="mt-2 bg-[#121212] border-[#2a2a2a] focus:border-[#c89f45]"
                data-testid="goal-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Cadence</Label>
                <div className="mt-2 flex gap-2">
                  {["weekly", "monthly"].map((c) => (
                    <button
                      key={c}
                      onClick={() => setForm({ ...form, cadence: c })}
                      className={`px-3 py-1.5 rounded-md text-xs capitalize transition-colors ${
                        form.cadence === c
                          ? "bg-[#c89f45] text-[#0a0a0a]"
                          : "bg-[#121212] border border-[#2a2a2a] text-[#aaa]"
                      }`}
                      data-testid={`goal-cadence-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Target date</Label>
                <Input
                  type="date"
                  value={form.target_date}
                  onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                  className="mt-2 bg-[#121212] border-[#2a2a2a] focus:border-[#c89f45]"
                  data-testid="goal-target-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white" data-testid="goal-cancel">
              Cancel
            </Button>
            <Button onClick={create} className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a]" data-testid="goal-save">
              Table it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function GoalTasks({ goal, onUpdated }) {
  const [expanded, setExpanded] = useState(Boolean(goal.tasks && goal.tasks.length > 0));
  const [newTask, setNewTask] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null); // taskId of in-flight toggle/delete
  const tasks = Array.isArray(goal.tasks) ? goal.tasks : [];

  const add = async (e) => {
    e?.preventDefault?.();
    const title = newTask.trim();
    if (!title || adding) return;
    setAdding(true);
    try {
      const res = await api.post(`/goals/${goal.id}/tasks`, { title });
      onUpdated(res.data);
      setNewTask("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.message || "Could not add task");
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (task) => {
    setBusy(task.id);
    try {
      const res = await api.patch(`/goals/${goal.id}/tasks/${task.id}`, { done: !task.done });
      onUpdated(res.data);
      if (!task.done && res.data.progress === 100) {
        toast.success(`"${goal.title}" — 100% complete. Table it as a win at your next session.`);
      }
    } catch (err) {
      toast.error("Could not update task");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (task) => {
    setBusy(task.id);
    try {
      const res = await api.delete(`/goals/${goal.id}/tasks/${task.id}`);
      onUpdated(res.data);
    } catch (err) {
      toast.error("Could not delete task");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-[#1a1a1a]" data-testid={`goal-tasks-${goal.id}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-[10px] font-mono uppercase tracking-widest text-[#8a8a8a] hover:text-[#c89f45] flex items-center gap-1.5 transition-colors"
        data-testid={`goal-tasks-toggle-${goal.id}`}
      >
        <ListChecks size={11} />
        {tasks.length === 0
          ? "BREAK IT DOWN INTO TASKS →"
          : `${expanded ? "HIDE" : "SHOW"} TASKS · ${tasks.filter((t) => t.done).length}/${tasks.length}`}
      </button>

      {expanded && (
        <div className="mt-4 space-y-2" data-testid={`goal-tasks-list-${goal.id}`}>
          {tasks.map((t) => (
            <div
              key={t.id}
              className="flex items-start gap-3 group"
              data-testid={`goal-task-${t.id}`}
            >
              <Checkbox
                checked={t.done}
                onCheckedChange={() => toggle(t)}
                disabled={busy === t.id}
                className="mt-0.5 border-[#333] data-[state=checked]:bg-[#c89f45] data-[state=checked]:border-[#c89f45] data-[state=checked]:text-[#0a0a0a]"
                data-testid={`goal-task-checkbox-${t.id}`}
              />
              <span
                className={`flex-1 text-sm leading-relaxed ${
                  t.done ? "text-[#8a8a8a] line-through decoration-[#c89f45]/60" : "text-[#ddd]"
                }`}
                data-testid={`goal-task-title-${t.id}`}
              >
                {t.title}
              </span>
              <button
                type="button"
                onClick={() => remove(t)}
                disabled={busy === t.id}
                className="opacity-0 group-hover:opacity-100 text-[#828282] hover:text-[#e07b6a] transition-all"
                aria-label="Delete task"
                data-testid={`goal-task-delete-${t.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}

          <form onSubmit={add} className="flex items-center gap-2 mt-3" data-testid={`goal-task-add-form-${goal.id}`}>
            <Input
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              placeholder="Add a task (e.g. Draft the auth spec)"
              maxLength={200}
              disabled={adding}
              className="flex-1 h-9 bg-[#0d0d0d] border-[#2a2a2a] focus:border-[#c89f45] text-sm"
              data-testid={`goal-task-input-${goal.id}`}
            />
            <Button
              type="submit"
              disabled={adding || !newTask.trim()}
              className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] h-9 px-3 rounded-md"
              data-testid={`goal-task-add-${goal.id}`}
            >
              <Plus size={14} className="mr-1" />
              {adding ? "Adding…" : "Add"}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
